import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { isRegisteredAccountType } from '../presentation/isRegisteredAccountType';

export type TaxableRealizedGainLine = {
  subAccountId: string;
  financialProductId: string;
  sellCents: number;
  estRealizedGainCents: number | null;
  isEstimateAvailable: boolean;
};

export type TaxableRealizedGainDetail = {
  lines: TaxableRealizedGainLine[];
  estRealizedGainCents: number;
  isComplete: boolean;
  incompleteLineCount: number;
};

export type TaxableRealizedGainEstimate = {
  estRealizedGainCents: number;
  /** False when any taxable sell line is missing sleeve ACB, quantity, or position data. */
  isComplete: boolean;
  lines: TaxableRealizedGainLine[];
  incompleteLineCount: number;
};

export const countTaxableSellLines = (
  audit: OptimizerAuditV2Record,
): number => {
  let count = 0;

  for (const subAccount of audit.subAccountResults ?? []) {
    if (isRegisteredAccountType(subAccount.accountType)) {
      continue;
    }

    for (const diff of subAccount.weightDiffs ?? []) {
      if ((diff.deltaCents ?? 0) < 0) {
        count += 1;
      }
    }
  }

  return count;
};

/** True when live report holding MV aligns with post-trade audit, not pre-trade. */
export const reportHoldingLooksPostTrade = (
  reportHoldingValueCents: number,
  preTradePositionValueCents: number,
  sellCents: number,
  sellRatio: number,
): boolean => {
  if (
    sellRatio <= 0 ||
    sellRatio >= 1 ||
    reportHoldingValueCents <= 0 ||
    preTradePositionValueCents <= 0
  ) {
    return false;
  }

  const postTradePositionValueCents = Math.max(
    0,
    preTradePositionValueCents - sellCents,
  );

  return (
    Math.abs(reportHoldingValueCents - postTradePositionValueCents) <
    Math.abs(reportHoldingValueCents - preTradePositionValueCents)
  );
};

/** Scale live book cost back to pre-trade when the report already reflects partial sells. */
export const derivePreTradeBookValueCents = (
  postTradeBookValueCents: number,
  sellRatio: number,
  reportLooksPostTrade: boolean,
): number => {
  if (
    !reportLooksPostTrade ||
    sellRatio <= 0 ||
    sellRatio >= 1 - Number.EPSILON
  ) {
    return postTradeBookValueCents;
  }

  return Math.round(postTradeBookValueCents / (1 - sellRatio));
};

type AuditSubAccountResult = NonNullable<
  OptimizerAuditV2Record['subAccountResults']
>[number];
type AuditWeightDiff = NonNullable<
  NonNullable<AuditSubAccountResult['weightDiffs']>[number]
>;

const resolveSleeveHolding = (
  report: PortfolioReport,
  subAccountId: string | null | undefined,
  financialProductId: string | null | undefined,
) => {
  if (!subAccountId || !financialProductId) {
    return null;
  }

  const sleeve = report.subAccounts.find(
    (entry) => entry.subAccount.id === subAccountId,
  );

  return (
    sleeve?.holdings.find(
      (holding) => holding.financialProduct.id === financialProductId,
    ) ?? null
  );
};

/** Reconstruct pre-trade MV from audit weights — not live report `valueCents`. */
export const derivePreTradePositionValueCents = (
  subAccount: AuditSubAccountResult,
  diff: AuditWeightDiff,
): number | null => {
  const deltaCents = diff.deltaCents ?? 0;
  if (deltaCents >= 0) {
    return null;
  }

  const sellCents = Math.abs(deltaCents);
  if (sellCents <= 0) {
    return null;
  }

  if (
    diff.inputPositionValueCents != null &&
    diff.inputPositionValueCents > 0
  ) {
    return diff.inputPositionValueCents;
  }

  const actualInvestable = Math.max(
    0,
    (subAccount.inputMarketValueCents ?? 0) - (subAccount.inputCashCents ?? 0),
  );
  const currentWeight = diff.currentWeight ?? 0;

  if (actualInvestable > 0 && currentWeight > 0) {
    return Math.round(currentWeight * actualInvestable);
  }

  // Full exit: sell proceeds approximate the pre-trade position MV.
  if ((diff.proposedWeight ?? 0) <= 0) {
    return sellCents;
  }

  return null;
};

const resolveRunTimeCostBasis = (diff: AuditWeightDiff) => {
  const adjustedCostBaseCents = diff.inputAdjustedCostBaseCents;
  const quantity = diff.inputQuantity;

  if (
    adjustedCostBaseCents == null ||
    adjustedCostBaseCents <= 0 ||
    quantity == null ||
    quantity <= 0
  ) {
    return null;
  }

  return { adjustedCostBaseCents, quantity };
};

export const buildTaxableRealizedGainDetail = (
  audit: OptimizerAuditV2Record,
  report: PortfolioReport,
): TaxableRealizedGainDetail | null => {
  const lines: TaxableRealizedGainLine[] = [];
  let estRealizedGainCents = 0;
  let isComplete = true;

  for (const subAccount of audit.subAccountResults ?? []) {
    if (isRegisteredAccountType(subAccount.accountType)) {
      continue;
    }

    for (const diff of subAccount.weightDiffs ?? []) {
      const deltaCents = diff.deltaCents ?? 0;
      if (deltaCents >= 0) {
        continue;
      }

      const sellCents = Math.abs(deltaCents);
      if (sellCents <= 0) {
        continue;
      }

      const subAccountId = subAccount.subAccountId ?? '';
      const financialProductId = diff.financialProductId ?? '';
      const positionValueCents = derivePreTradePositionValueCents(
        subAccount,
        diff,
      );

      if (
        !subAccountId ||
        !financialProductId ||
        positionValueCents == null ||
        positionValueCents <= 0
      ) {
        isComplete = false;
        lines.push({
          subAccountId,
          financialProductId,
          sellCents,
          estRealizedGainCents: null,
          isEstimateAvailable: false,
        });
        continue;
      }

      const runTimeCostBasis = resolveRunTimeCostBasis(diff);
      const holding = runTimeCostBasis
        ? null
        : resolveSleeveHolding(
            report,
            subAccount.subAccountId,
            diff.financialProductId,
          );
      const adjustedCostBaseCents =
        runTimeCostBasis?.adjustedCostBaseCents ??
        holding?.adjustedCostBaseCents;
      const quantity = runTimeCostBasis?.quantity ?? holding?.quantity ?? 0;

      if (!adjustedCostBaseCents || adjustedCostBaseCents <= 0) {
        isComplete = false;
        lines.push({
          subAccountId,
          financialProductId,
          sellCents,
          estRealizedGainCents: null,
          isEstimateAvailable: false,
        });
        continue;
      }

      if (quantity <= 0) {
        isComplete = false;
        lines.push({
          subAccountId,
          financialProductId,
          sellCents,
          estRealizedGainCents: null,
          isEstimateAvailable: false,
        });
        continue;
      }

      const sellRatio = Math.min(1, sellCents / positionValueCents);
      const postTradeBookValueCents = Math.round(
        quantity * adjustedCostBaseCents,
      );
      const reportLooksPostTrade =
        runTimeCostBasis == null &&
        reportHoldingLooksPostTrade(
          holding?.valueCents ?? 0,
          positionValueCents,
          sellCents,
          sellRatio,
        );
      const bookValueCents = derivePreTradeBookValueCents(
        postTradeBookValueCents,
        sellRatio,
        reportLooksPostTrade,
      );
      const lineGainCents = Math.round(
        sellRatio * Math.max(0, positionValueCents - bookValueCents),
      );

      estRealizedGainCents += lineGainCents;
      lines.push({
        subAccountId,
        financialProductId,
        sellCents,
        estRealizedGainCents: lineGainCents,
        isEstimateAvailable: true,
      });
    }
  }

  if (lines.length === 0) {
    return null;
  }

  lines.sort(
    (a, b) =>
      b.sellCents - a.sellCents ||
      a.subAccountId.localeCompare(b.subAccountId) ||
      a.financialProductId.localeCompare(b.financialProductId),
  );

  return {
    lines,
    estRealizedGainCents,
    isComplete,
    incompleteLineCount: lines.filter((line) => !line.isEstimateAvailable)
      .length,
  };
};

export const estimateTaxableRealizedGainCents = (
  audit: OptimizerAuditV2Record,
  report: PortfolioReport,
): TaxableRealizedGainEstimate | null => {
  const detail = buildTaxableRealizedGainDetail(audit, report);
  if (!detail) {
    return null;
  }

  return {
    estRealizedGainCents: detail.estRealizedGainCents,
    isComplete: detail.isComplete,
    lines: detail.lines,
    incompleteLineCount: detail.incompleteLineCount,
  };
};

export const countEstimatedGainLines = (
  estimate: TaxableRealizedGainEstimate,
): number => estimate.lines.filter((line) => line.isEstimateAvailable).length;
